import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import useMedsiWebMonitor, {
  type MedsiMonitorCompletePayload,
} from 'hooks/medsi/useMedsiWebMonitor';
import usePageVisibility from 'hooks/usePageVisibility';
import { getCurrentTimestamp, isMilestoneCompleted } from 'lib/utils';
import { mapUserAttributesToRppgDemographics } from 'lib/medsi/demographics';
import { mapRppgFinalResultToBinahData } from 'lib/medsi/mapRppgToBinahData';
import { SessionState } from 'lib/medsi/scan-compat';
import { getMedsiScanConfig } from 'lib/medsi/config';
import useAuthStore from 'store/auth';
import { captureUserActivity } from 'services/profile/api';
import { USER_ACTIVITY } from '../../../types';
import { patchScanSession, postHealthReading } from 'services/face-scan/api';
import ScanErrorDialog from 'components/dialogs/ScanErrorDialog';
import Loader from 'components/Loader';
import { PATH_ROUTES } from 'routes/path';
import MedsiScan from './medsi/MedsiScan';
import { usePostOnboardingStep } from 'services/dashboard/hooks';
import { MILESTONES } from 'lib/constants';

export type ValidityCount = {
  [key: string]: number;
};

export default function MedsiMonitor() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const userAttributes = useAuthStore(state => state.userAttributes);

  const videoRef = useRef<HTMLVideoElement>(null);

  const [startMeasuring, setStartMeasuring] = useState(false);
  const [imageValidityJSON, setImageValidityJSON] = useState<ValidityCount>({});
  const [readingId, setReadingId] = useState('');
  const [showErrorDialog, setShowErrorDialog] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isStopping, setIsStopping] = useState(false);
  const [completedPayload, setCompletedPayload] =
    useState<MedsiMonitorCompletePayload | null>(null);
  const submittedReadingIdRef = useRef<string | null>(null);

  const demographics = useMemo(
    () => mapUserAttributesToRppgDemographics(userAttributes),
    [userAttributes],
  );

  const handleValidityJSON = useCallback((validity: string) => {
    setImageValidityJSON(prev => ({
      ...prev,
      [validity]: (prev[validity] || 0) + 1,
    }));
  }, []);

  const onComplete = useCallback((payload: MedsiMonitorCompletePayload) => {
    if (
      !payload.readingId ||
      submittedReadingIdRef.current === payload.readingId
    ) {
      return;
    }

    submittedReadingIdRef.current = payload.readingId;
    setStartMeasuring(false);
    setCompletedPayload(payload);
  }, []);

  const {
    sessionState,
    vitalSigns,
    error,
    info,
    readingId: orchestratorReadingId,
    phase,
    progress,
    stopScan,
  } = useMedsiWebMonitor(
    videoRef,
    startMeasuring,
    onComplete,
    handleValidityJSON,
    demographics,
  );

  const isPageVisible = usePageVisibility();

  const isCameraReady = sessionState === SessionState.ACTIVE;
  const isMeasuring = useCallback(
    () =>
      !isStopping &&
      (phase === 'initializing' ||
        phase === 'scanning' ||
        (startMeasuring &&
          phase !== 'processing' &&
          phase !== 'done' &&
          phase !== 'error')),
    [isStopping, startMeasuring, phase],
  );

  const showResultsLoader =
    isSubmitting || phase === 'processing' || phase === 'done';

  const { mutateAsync: updateOnboarding } = usePostOnboardingStep();
  const { mutate, data: postHealthRes } = useMutation({
    mutationFn: postHealthReading,
    onSuccess: async (data: any) => {
      if (!data.success) {
        try {
          await captureUserActivity(USER_ACTIVITY.SCAN_ERROR, {
            message: imageValidityJSON,
            reading_id: readingId,
          });
          await patchScanSession({
            session_id: readingId,
            status: USER_ACTIVITY.SCAN_ERROR,
          });
        } catch (err) {
          console.error(err);
        }
        setShowErrorDialog(true);
        setIsSubmitting(false);
        return;
      }

      try {
        if (!isMilestoneCompleted(MILESTONES.QUESTIONNAIRE)) {
          await updateOnboarding({ milestone: MILESTONES.QUESTIONNAIRE });
        }
        if (
          !isMilestoneCompleted(MILESTONES.ONBOARDED) &&
          isMilestoneCompleted(MILESTONES.FIRST_HEALTH_MEASUREMENT)
        ) {
          await updateOnboarding({ milestone: MILESTONES.ONBOARDED });
        }

        captureUserActivity(USER_ACTIVITY.END_SCAN, {
          reading_id: readingId,
        });
        await patchScanSession({
          session_id: readingId,
          status: USER_ACTIVITY.END_SCAN,
        });
      } catch (err) {
        console.error(err);
      }
      setIsSubmitting(false);
      navigate(PATH_ROUTES.FACE_SCAN_RESULT);
    },
    onError: () => {
      submittedReadingIdRef.current = null;
      setIsSubmitting(false);
    },
    onSettled: () => setIsSubmitting(false),
  });

  useEffect(() => {
    if (!completedPayload) return;

    const { finalResult, readingId: completedReadingId, imageValidityCounts } =
      completedPayload;
    setReadingId(completedReadingId);
    setIsSubmitting(true);

    const scanData = mapRppgFinalResultToBinahData(
      finalResult as unknown as Record<string, unknown>,
    );
    const validityErrors =
      Object.keys(imageValidityCounts).length > 0
        ? imageValidityCounts
        : imageValidityJSON;

    mutate({
      data: scanData,
      scan_error: validityErrors,
      timestamp: getCurrentTimestamp(),
      reading_id: completedReadingId,
      binaah_key: getMedsiScanConfig().rppgApiKey,
    });
  }, [completedPayload, imageValidityJSON, mutate]);

  useEffect(() => {
    if (!isStopping) return;
    if (phase === 'idle' && isCameraReady) {
      setIsStopping(false);
    }
  }, [isStopping, phase, isCameraReady]);

  const onMeasure = useCallback(async () => {
    if (isStopping) return;

    if (isMeasuring()) {
      setIsStopping(true);
      setStartMeasuring(false);
      try {
        captureUserActivity(USER_ACTIVITY.STOP_SCAN, {
          message: 'restart session',
          reading_id: readingId,
        });
        await patchScanSession({
          session_id: readingId,
          status: USER_ACTIVITY.STOP_SCAN,
        });
      } catch (err) {
        console.error(err);
      }
      void stopScan().catch(err => {
        console.error('[MedsiMonitor] stopScan failed:', err);
        setIsStopping(false);
      });
      return;
    }

    if (isCameraReady) {
      const sessionId = orchestratorReadingId ?? readingId;
      if (!sessionId) return;

      try {
        await patchScanSession({
          session_id: sessionId,
          status: USER_ACTIVITY.START_SCAN,
        });
        captureUserActivity(USER_ACTIVITY.START_SCAN, {
          reading_id: sessionId,
        });
      } catch (err) {
        console.error(err);
        return;
      }

      setReadingId(sessionId);
      setStartMeasuring(true);
    }
  }, [
    isCameraReady,
    isMeasuring,
    isStopping,
    orchestratorReadingId,
    readingId,
    stopScan,
  ]);

  useEffect(() => {
    if (!isPageVisible && isMeasuring() && document.hidden) {
      setIsStopping(true);
      setStartMeasuring(false);
      void stopScan().catch(err => {
        console.error('[MedsiMonitor] stopScan failed:', err);
        setIsStopping(false);
      });
    }
  }, [isPageVisible, isMeasuring, stopScan]);

  useEffect(() => {
    if (error?.code === -1 || !error?.message) return;
    setStartMeasuring(false);
    setIsStopping(false);
  }, [error]);

  useEffect(() => {
    return () => navigate(0);
  }, []);

  const onErrorClose = () => {
    setShowErrorDialog(false);
    navigate(0);
    navigate(`${PATH_ROUTES.FACE_SCAN}?showScan=true`);
  };

  const onRescan = () => {
    setShowErrorDialog(false);
    onMeasure();
  };

  return (
    <>
      <div className='relative h-[calc(100svh-4rem)] max-h-[calc(100svh-4rem)] overflow-hidden'>
        {showResultsLoader && (
          <div className='fixed inset-0 z-50 flex flex-col items-center justify-center gap-4 bg-white'>
            <Loader className='h-auto' />
            <p className='font-medium text-center text-muted-foreground'>
              {t('medsi_processing_results')}
            </p>
          </div>
        )}
        <MedsiScan
          info={info}
          isCameraReady={isCameraReady}
          isMeasuring={isMeasuring}
          isStopping={isStopping}
          onMeasure={onMeasure}
          videoRef={videoRef}
          vitalSigns={vitalSigns}
          scanProgress={progress}
          scanError={error?.code !== -1 ? error.message : undefined}
        />
      </div>

      <ScanErrorDialog
        open={showErrorDialog}
        onClose={onErrorClose}
        onRescan={onRescan}
        error={postHealthRes}
      />
    </>
  );
}
